import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from './Button';
import { IconButton } from './IconButton';

/** Primary action plus a dropdown of `options`, each {label, icon, onClick}. */
export const SplitButton = React.memo(
    ({
        children,
        onClick,
        options = [],
        variant = 'primary',
        size = 'medium',
        disabled = false,
        icon = null,
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const dropdownRef = useRef(null);

        const handleToggle = useCallback(() => {
            setIsOpen(prev => !prev);
        }, []);

        const handleOptionClick = useCallback(optionOnClick => {
            optionOnClick();
            setIsOpen(false);
        }, []);

        return (
            <div className="relative inline-block" ref={dropdownRef}>
                <div className="inline-flex items-center">
                    <Button
                        onClick={onClick}
                        variant={variant}
                        size={size}
                        disabled={disabled}
                        icon={icon}
                        className="rounded-r-none"
                    >
                        {children}
                    </Button>
                    <IconButton
                        icon={isOpen ? 'expand_less' : 'expand_more'}
                        variant={variant}
                        size={size}
                        disabled={disabled}
                        onClick={handleToggle}
                        aria-label="Show more options"
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        className="rounded-l-none -ml-px"
                    />
                </div>

                {isOpen && options.length > 0 && (
                    <div
                        className="absolute right-0 mt-1 min-w-[12rem] bg-surface-elevated border border-border rounded-lg shadow-lg z-dropdown py-1"
                        role="menu"
                    >
                        {options.map((option, index) => (
                            <button
                                key={index}
                                className="touch-target flex w-full items-center gap-2 px-3 py-2 text-sm text-fg text-left hover:bg-surface-alt"
                                onClick={() => handleOptionClick(option.onClick)}
                                role="menuitem"
                            >
                                {option.icon && (
                                    <span className="material-symbols-outlined">{option.icon}</span>
                                )}
                                <span>{option.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
);

SplitButton.displayName = 'SplitButton';

SplitButton.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            icon: PropTypes.string,
            onClick: PropTypes.func.isRequired,
        })
    ),
    variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'ghost']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    disabled: PropTypes.bool,
    icon: PropTypes.string,
};
